import React, { useState, useEffect } from 'react';
import { X, FileCheck, Loader2, Check, Contact, GraduationCap, FileText, Award, ClipboardList } from 'lucide-react';
import { adminUpdateStudentProfile, adminGetStudentProfile } from '@/services/profileService';
import type { StudentUser } from '@/services/userService';

interface StudentPhysicalDocumentsTrackingModalProps {
  isOpen: boolean;
  onClose: () => void;
  student: StudentUser;
  onSuccess: () => void;
}

interface StudentPhysicalDocsState {
  // Required documents
  physicalDocStudentCin: boolean;
  physicalDocStudentBac: boolean;
  physicalDocStudentReleveBac: boolean;
  // Conditional documents
  physicalDocStudentDernierDiplome: boolean;
  physicalDocStudentReleveDiplome: boolean;
  physicalDocStudentReleveAnneesPrecedentes: boolean;
  // Optional documents
  physicalDocStudentCv: boolean;
  physicalDocStudentLettreMotivation: boolean;
}

interface StudentAcademicInfo {
  diplomeAcces?: string;
  niveauFormation?: number;
  niveauFormationInitial?: number;
  premiereAnneeInscription?: string;
}

// Helper to get current academic year (format: "2025/2026")
const getCurrentAcademicYear = (): string => {
  const now = new Date();
  const year = now.getFullYear();
  const month = now.getMonth();
  // Academic year starts in September (month 8)
  if (month >= 8) {
    return `${year}/${year + 1}`;
  }
  return `${year - 1}/${year}`;
};

// Helper to check if diplome is BAC or similar
const isBacDiplome = (diplome?: string): boolean => {
  if (!diplome) return true; // Default to BAC if not specified
  const normalized = diplome.toLowerCase().trim();
  return normalized === 'bac' ||
         normalized === 'baccalauréat' ||
         normalized === 'baccalaureat' ||
         normalized.startsWith('bac ') ||
         normalized.includes('baccalaur');
};

export const StudentPhysicalDocumentsTrackingModal: React.FC<StudentPhysicalDocumentsTrackingModalProps> = ({
  isOpen,
  onClose,
  student,
  onSuccess,
}) => {
  const [loading, setLoading] = useState(false);
  const [fetchingData, setFetchingData] = useState(true);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [docs, setDocs] = useState<StudentPhysicalDocsState>({
    physicalDocStudentCin: false,
    physicalDocStudentBac: false,
    physicalDocStudentReleveBac: false,
    physicalDocStudentDernierDiplome: false,
    physicalDocStudentReleveDiplome: false,
    physicalDocStudentReleveAnneesPrecedentes: false,
    physicalDocStudentCv: false,
    physicalDocStudentLettreMotivation: false,
  });

  // Academic info for conditional document requirements
  const [academicInfo, setAcademicInfo] = useState<StudentAcademicInfo>({
    diplomeAcces: undefined,
    niveauFormation: undefined,
    premiereAnneeInscription: undefined,
  });

  // Determine conditional document requirements
  // Student enrolled with non-BAC degree needs to submit that degree and its transcript
  const hasNonBacDiplome = !isBacDiplome(academicInfo.diplomeAcces);

  // Student needs previous years' transcripts ONLY if they ENROLLED at a level > 1 (transfer student)
  // Use niveauFormationInitial to check what level they started at
  // If they started at year 1 and got promoted to year 2+, they're NOT a transfer (grades in our system)
  const isTransferStudent = (academicInfo.niveauFormationInitial ?? 1) > 1;
  const needsPreviousYearsTranscripts = isTransferStudent;

  // Fetch current document status when modal opens
  useEffect(() => {
    if (isOpen && student) {
      fetchCurrentStatus();
    }
  }, [isOpen, student?.id]);

  const fetchCurrentStatus = async () => {
    setFetchingData(true);
    try {
      // Fetch student profile to get current physical document status
      const data = await adminGetStudentProfile(student.id);

      if (data) {
        // Set physical document status from profile
        if (data.profile) {
          setDocs({
            physicalDocStudentCin: data.profile.physicalDocStudentCin ?? false,
            physicalDocStudentBac: data.profile.physicalDocStudentBac ?? false,
            physicalDocStudentReleveBac: data.profile.physicalDocStudentReleveBac ?? false,
            physicalDocStudentDernierDiplome: data.profile.physicalDocStudentDernierDiplome ?? false,
            physicalDocStudentReleveDiplome: data.profile.physicalDocStudentReleveDiplome ?? false,
            physicalDocStudentReleveAnneesPrecedentes: data.profile.physicalDocStudentReleveAnneesPrecedentes ?? false,
            physicalDocStudentCv: data.profile.physicalDocStudentCv ?? false,
            physicalDocStudentLettreMotivation: data.profile.physicalDocStudentLettreMotivation ?? false,
          });
        }

        // Set academic info from student data
        if (data.student) {
          setAcademicInfo({
            diplomeAcces: data.student.diplomeAcces,
            niveauFormation: data.student.niveauFormation,
            niveauFormationInitial: data.student.niveauFormationInitial,
            premiereAnneeInscription: data.student.premiereAnneeInscription,
          });
        }
      }
    } catch (err) {
      console.error('Error fetching physical documents status:', err);
    } finally {
      setFetchingData(false);
    }
  };

  const handleSave = async () => {
    setLoading(true);
    setError(null);

    try {
      await adminUpdateStudentProfile(student.id, {
        physicalDocStudentCin: docs.physicalDocStudentCin,
        physicalDocStudentBac: docs.physicalDocStudentBac,
        physicalDocStudentReleveBac: docs.physicalDocStudentReleveBac,
        physicalDocStudentDernierDiplome: docs.physicalDocStudentDernierDiplome,
        physicalDocStudentReleveDiplome: docs.physicalDocStudentReleveDiplome,
        physicalDocStudentReleveAnneesPrecedentes: docs.physicalDocStudentReleveAnneesPrecedentes,
        physicalDocStudentCv: docs.physicalDocStudentCv,
        physicalDocStudentLettreMotivation: docs.physicalDocStudentLettreMotivation,
      });

      setSuccess(true);
      setTimeout(() => {
        onSuccess();
        onClose();
      }, 1000);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Une erreur est survenue');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      setError(null);
      setSuccess(false);
      onClose();
    }
  };

  const toggleDoc = (key: keyof StudentPhysicalDocsState) => {
    setDocs(prev => ({ ...prev, [key]: !prev[key] }));
  };

  // Calculate document completion - only count required documents and applicable conditional ones
  const calculateCompletion = () => {
    let received = 0;
    let total = 3; // Required: CIN, BAC, Releve BAC

    // Count required documents
    if (docs.physicalDocStudentCin) received++;
    if (docs.physicalDocStudentBac) received++;
    if (docs.physicalDocStudentReleveBac) received++;

    // Conditional: non-BAC diploma
    if (hasNonBacDiplome) {
      total += 2; // Dernier diplome + Releve diplome
      if (docs.physicalDocStudentDernierDiplome) received++;
      if (docs.physicalDocStudentReleveDiplome) received++;
    }

    // Conditional: previous years transcripts
    if (needsPreviousYearsTranscripts) {
      total += 1;
      if (docs.physicalDocStudentReleveAnneesPrecedentes) received++;
    }

    // CV and Lettre de motivation are optional - don't count toward completion
    return { received, total };
  };

  const { received: receivedCount, total: totalDocs } = calculateCompletion();
  const completionPercentage = (receivedCount / totalDocs) * 100;
  const isComplete = receivedCount === totalDocs;

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex min-h-full items-center justify-center p-4">
        {/* Backdrop */}
        <div
          className="fixed inset-0 bg-black/50 transition-opacity"
          onClick={handleClose}
        />

        {/* Modal */}
        <div className="relative bg-white rounded-xl shadow-xl w-full max-w-lg transform transition-all">
          {/* Header */}
          <div className="flex items-center justify-between p-4 border-b border-gray-200 bg-gradient-to-r from-blue-600 to-indigo-600 rounded-t-xl">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-white/20 rounded-lg">
                <FileCheck className="text-white" size={20} />
              </div>
              <div>
                <h2 className="text-lg font-semibold text-white">
                  Suivi des documents physiques
                </h2>
                <p className="text-sm text-blue-100">
                  {student.firstName} {student.lastName}
                </p>
              </div>
            </div>
            <button
              onClick={handleClose}
              disabled={loading}
              className="p-2 hover:bg-white/20 rounded-lg transition-colors text-white"
            >
              <X size={20} />
            </button>
          </div>

          {/* Content */}
          <div className="p-6 max-h-[70vh] overflow-y-auto">
            {fetchingData ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="animate-spin text-blue-600" size={32} />
              </div>
            ) : (
              <>
                {/* Progress indicator */}
                <div className="mb-6 p-4 bg-gray-50 rounded-lg">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-medium text-gray-700">Documents physiques requis</span>
                    <span className={`text-sm font-semibold ${isComplete ? 'text-green-600' : 'text-blue-600'}`}>
                      {receivedCount} / {totalDocs}
                    </span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div
                      className={`h-2 rounded-full transition-all duration-300 ${isComplete ? 'bg-green-500' : 'bg-blue-600'}`}
                      style={{ width: `${completionPercentage}%` }}
                    />
                  </div>
                  {isComplete && (
                    <p className="text-xs text-green-600 mt-2 flex items-center gap-1">
                      <Check size={14} />
                      Tous les documents requis sont recus
                    </p>
                  )}
                </div>

                {/* Academic context */}
                <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg text-sm">
                  <p className="font-medium text-blue-900 mb-1">Contexte academique:</p>
                  <div className="text-blue-700 space-y-1">
                    <p>Diplome d'acces: <span className="font-medium">{academicInfo.diplomeAcces || 'BAC'}</span></p>
                    <p>Niveau: <span className="font-medium">{academicInfo.niveauFormation ? `${academicInfo.niveauFormation}ere/eme annee` : '1ere annee'}</span></p>
                  </div>
                </div>

                {error && (
                  <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
                    {error}
                  </div>
                )}

                {/* Document checkboxes */}
                <div className="space-y-3">
                  {/* Section: Required Documents */}
                  <div className="mb-2">
                    <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wide mb-2">
                      Documents requis
                    </h3>
                  </div>

                  {/* CIN */}
                  <label className="flex items-center gap-3 p-3 border border-gray-200 rounded-lg hover:bg-gray-50 cursor-pointer transition-colors">
                    <input
                      type="checkbox"
                      checked={docs.physicalDocStudentCin}
                      onChange={() => toggleDoc('physicalDocStudentCin')}
                      className="w-5 h-5 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                    />
                    <Contact className="text-gray-500" size={20} />
                    <div className="flex-1">
                      <span className="font-medium text-gray-900">CIN / Passeport</span>
                      <p className="text-xs text-gray-500">Copie de la carte d'identite ou du passeport</p>
                    </div>
                    {docs.physicalDocStudentCin && <Check className="text-blue-600" size={18} />}
                  </label>

                  {/* BAC */}
                  <label className="flex items-center gap-3 p-3 border border-gray-200 rounded-lg hover:bg-gray-50 cursor-pointer transition-colors">
                    <input
                      type="checkbox"
                      checked={docs.physicalDocStudentBac}
                      onChange={() => toggleDoc('physicalDocStudentBac')}
                      className="w-5 h-5 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                    />
                    <GraduationCap className="text-gray-500" size={20} />
                    <div className="flex-1">
                      <span className="font-medium text-gray-900">Baccalaureat</span>
                      <p className="text-xs text-gray-500">Copie du diplome du baccalaureat</p>
                    </div>
                    {docs.physicalDocStudentBac && <Check className="text-blue-600" size={18} />}
                  </label>

                  {/* Releve BAC */}
                  <label className="flex items-center gap-3 p-3 border border-gray-200 rounded-lg hover:bg-gray-50 cursor-pointer transition-colors">
                    <input
                      type="checkbox"
                      checked={docs.physicalDocStudentReleveBac}
                      onChange={() => toggleDoc('physicalDocStudentReleveBac')}
                      className="w-5 h-5 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                    />
                    <ClipboardList className="text-gray-500" size={20} />
                    <div className="flex-1">
                      <span className="font-medium text-gray-900">Releve de notes du BAC</span>
                      <p className="text-xs text-gray-500">Releve de notes du baccalaureat</p>
                    </div>
                    {docs.physicalDocStudentReleveBac && <Check className="text-blue-600" size={18} />}
                  </label>

                  {/* Section: Conditional Documents */}
                  {(hasNonBacDiplome || needsPreviousYearsTranscripts) && (
                    <>
                      <div className="mt-4 mb-2">
                        <h3 className="text-sm font-semibold text-amber-700 uppercase tracking-wide mb-2">
                          Documents conditionnels
                        </h3>
                      </div>

                      {/* Dernier diplome (non-BAC) */}
                      {hasNonBacDiplome && (
                        <>
                          <label className="flex items-center gap-3 p-3 border border-amber-200 bg-amber-50 rounded-lg hover:bg-amber-100 cursor-pointer transition-colors">
                            <input
                              type="checkbox"
                              checked={docs.physicalDocStudentDernierDiplome}
                              onChange={() => toggleDoc('physicalDocStudentDernierDiplome')}
                              className="w-5 h-5 text-amber-600 border-amber-300 rounded focus:ring-amber-500"
                            />
                            <Award className="text-amber-600" size={20} />
                            <div className="flex-1">
                              <span className="font-medium text-amber-900">Dernier diplome ({academicInfo.diplomeAcces})</span>
                              <p className="text-xs text-amber-700">Copie du diplome d'acces (requis car non-BAC)</p>
                            </div>
                            {docs.physicalDocStudentDernierDiplome && <Check className="text-amber-600" size={18} />}
                          </label>

                          <label className="flex items-center gap-3 p-3 border border-amber-200 bg-amber-50 rounded-lg hover:bg-amber-100 cursor-pointer transition-colors">
                            <input
                              type="checkbox"
                              checked={docs.physicalDocStudentReleveDiplome}
                              onChange={() => toggleDoc('physicalDocStudentReleveDiplome')}
                              className="w-5 h-5 text-amber-600 border-amber-300 rounded focus:ring-amber-500"
                            />
                            <ClipboardList className="text-amber-600" size={20} />
                            <div className="flex-1">
                              <span className="font-medium text-amber-900">Releve de notes du diplome</span>
                              <p className="text-xs text-amber-700">Releve de notes du dernier diplome</p>
                            </div>
                            {docs.physicalDocStudentReleveDiplome && <Check className="text-amber-600" size={18} />}
                          </label>
                        </>
                      )}

                      {/* Previous years transcripts */}
                      {needsPreviousYearsTranscripts && (
                        <label className="flex items-center gap-3 p-3 border border-amber-200 bg-amber-50 rounded-lg hover:bg-amber-100 cursor-pointer transition-colors">
                          <input
                            type="checkbox"
                            checked={docs.physicalDocStudentReleveAnneesPrecedentes}
                            onChange={() => toggleDoc('physicalDocStudentReleveAnneesPrecedentes')}
                            className="w-5 h-5 text-amber-600 border-amber-300 rounded focus:ring-amber-500"
                          />
                          <ClipboardList className="text-amber-600" size={20} />
                          <div className="flex-1">
                            <span className="font-medium text-amber-900">Releves de notes annees precedentes</span>
                            <p className="text-xs text-amber-700">
                              Requis car etudiant en {academicInfo.niveauFormation}eme annee
                            </p>
                          </div>
                          {docs.physicalDocStudentReleveAnneesPrecedentes && <Check className="text-amber-600" size={18} />}
                        </label>
                      )}
                    </>
                  )}

                  {/* Section: Optional Documents */}
                  <div className="mt-4 mb-2">
                    <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-2">
                      Documents optionnels
                      <span className="ml-2 text-xs font-normal normal-case">(ne comptent pas dans le pourcentage)</span>
                    </h3>
                  </div>

                  {/* CV */}
                  <label className="flex items-center gap-3 p-3 border border-gray-200 rounded-lg hover:bg-gray-50 cursor-pointer transition-colors opacity-80">
                    <input
                      type="checkbox"
                      checked={docs.physicalDocStudentCv}
                      onChange={() => toggleDoc('physicalDocStudentCv')}
                      className="w-5 h-5 text-gray-500 border-gray-300 rounded focus:ring-gray-400"
                    />
                    <FileText className="text-gray-400" size={20} />
                    <div className="flex-1">
                      <span className="font-medium text-gray-700">CV</span>
                      <p className="text-xs text-gray-500">Curriculum Vitae (optionnel)</p>
                    </div>
                    {docs.physicalDocStudentCv && <Check className="text-gray-500" size={18} />}
                  </label>

                  {/* Lettre de motivation */}
                  <label className="flex items-center gap-3 p-3 border border-gray-200 rounded-lg hover:bg-gray-50 cursor-pointer transition-colors opacity-80">
                    <input
                      type="checkbox"
                      checked={docs.physicalDocStudentLettreMotivation}
                      onChange={() => toggleDoc('physicalDocStudentLettreMotivation')}
                      className="w-5 h-5 text-gray-500 border-gray-300 rounded focus:ring-gray-400"
                    />
                    <FileText className="text-gray-400" size={20} />
                    <div className="flex-1">
                      <span className="font-medium text-gray-700">Lettre de motivation</span>
                      <p className="text-xs text-gray-500">Lettre de motivation (optionnel)</p>
                    </div>
                    {docs.physicalDocStudentLettreMotivation && <Check className="text-gray-500" size={18} />}
                  </label>
                </div>
              </>
            )}
          </div>

          {/* Footer */}
          <div className="flex justify-end gap-3 p-4 border-t border-gray-200 bg-gray-50 rounded-b-xl">
            <button
              type="button"
              onClick={handleClose}
              disabled={loading}
              className="px-4 py-2 text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-100 transition-colors disabled:opacity-50"
            >
              Annuler
            </button>
            <button
              type="button"
              onClick={handleSave}
              disabled={loading || success || fetchingData}
              className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
            >
              {loading ? (
                <>
                  <Loader2 className="animate-spin" size={18} />
                  <span>Enregistrement...</span>
                </>
              ) : success ? (
                <>
                  <Check size={18} />
                  <span>Enregistre !</span>
                </>
              ) : (
                <span>Enregistrer</span>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default StudentPhysicalDocumentsTrackingModal;
